import { useCallback, useLayoutEffect, useRef } from "react";

type Handler<TArgs extends any[], TReturn> = (...args: TArgs) => TReturn;

/**
 * From React RFC: https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md
 * A Hook to define an event handler with an always-stable function identity.
 */
export function useEvent<TArgs extends any[], TReturn>(handler: Handler<TArgs, TReturn>): Handler<TArgs, TReturn> {
    const handlerRef = useRef<Handler<TArgs, TReturn>>(null);

    // In a real implementation, this would run before layout effects
    useLayoutEffect(() => {
        handlerRef.current = handler;
    });

    return useCallback((...args: TArgs) => {
        // In a real implementation, this would throw if called during render
        const fn = handlerRef.current;
        return fn(...args);
    }, []);
}
